import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
} from '@scio3/ui';
import { START_TRIAL_ADDON, UPDATE_SUBSCRIPTION_ADDON } from '@scio3/graphql-client';
import { DEVICE_CODE_STATUS, PLAN_FEATURES } from '@scio3/shared-types';
import { useBillingDialogStore, openUpgradeWizard } from '../stores/billing-dialog-store';
import { useBillingData } from '../hooks/useBillingData';
import { extractMutationResponse } from '../utils/plan-helpers';
import { ADDON_DISPLAY_NAMES } from '../utils/billing-constants';
import { ProcessingOverlay } from '../components/ProcessingOverlay';
import { useAddOnPricing } from '@/features/screens/hooks/useAddOnPricing';
import { resolveAddonRequired } from '@/hooks/useSubscriptionModal';
import { usePermissions } from '@/hooks/usePermissions';

/** Device entry inside `dialogContext.items[].devices` or `dialogContext.devices`. */
type AddonDevice = {
  name?: string;
  _id?: string;
};

/** Shape of each element in `dialogContext.items` — backend returns one per addon that failed. */
type AddonItem = {
  key?: string;
  /** Backend-canonical addon key (matches `key` but used by some legacy payloads). */
  addOnKey?: string;
  devices?: AddonDevice[];
  /**
   * Exact license deficit computed by the backend — number of NEW licenses
   * needed (accounts for already-available unused licenses). Preferred over
   * `devices.length` because the devices array can include screens that
   * already have licenses covering them. Returned by `optisigns-open-api`
   * at device.service.ts:3575, 3616, 3915, 3955.
   */
  maxQuantityNeedIncrease?: number;
  /** Existing paid-license capacity (gates whether Add Licenses is shown — scio1 parity). */
  maxQuantity?: number;
  /** Backend error code for this specific addon (used by 2002 tier-mismatch branch). */
  code?: number;
  /** Whether this addon is on trial (gates 2002 footer button selection). */
  isTrial?: boolean;
  /** Whether the company itself is on a trial plan (different from addon trial). */
  isCompanyTrial?: boolean;
  /** Whether the company has a card on file. */
  haveCard?: boolean;
};

/**
 * Add-on required / activation modal (PW02).
 *
 * scio1 parity: header "Add-ons", lists affected devices. Branches on the
 * account's current add-on state:
 *   - No active license     → 3 actions (No / Add Licenses / Activate Trial)
 *                             with the 14-day trial pitch.
 *   - Already active        → 2 actions (No / Add Licenses) with the
 *                             "update your license" copy. Hides the trial
 *                             button because the trial is already consumed
 *                             or the account is past it.
 *
 * Opened by `useSubscriptionModal.openAddOnRequired` when an addon-license
 * error comes back from the backend (e.g. from `addWidgetAddonDevices`).
 * Context payload carries `addonType`, `devices`, `items`, `subAccountId`.
 */
export function AddonRequiredDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);

  const addonKey = (dialogContext.addonType as string) ?? '';
  const addonName = ADDON_DISPLAY_NAMES[addonKey] ?? addonKey;
  const subAccountId = dialogContext.subAccountId as string | undefined;
  // #1181 — SCIO1 parity (aericastAssignModal.js:73-82): when push hits
  // multiple addons (e.g. asset uses both AeriCast and VideoWall), the body
  // copy joins their names with ", ". Falls back to the single `addonName`
  // when items[] isn't populated. Built lazily later from `items`.

  const isOpen = activeDialog === 'addonRequired';

  // Resolve current add-on license state from billing data so we can branch
  // between the trial-pitch variant and the update-license variant.
  // Skip the query when the dialog is closed — useBillingData's auth-store
  // fallback is instant either way.
  const { addons, planFeature, currency, currencySymbol } = useBillingData({
    subAccountId,
    skip: !isOpen,
  });
  const thisAddon = addons.find((a) => a.key === addonKey);

  // Trial availability — primary signal is the backend `isTrial` flag from the
  // exception payload (#1174). It directly reflects whether the user is currently
  // on trial / can start one (`true`) vs. has a paid add-on subscription (`false`).
  //
  // Backend overloads `codeStatus` 1000 for both "trial available" AND "paid sub,
  // no devices using add-on yet" — without consulting `isTrial`, a paid subscriber
  // sees an irrelevant Activate Trial button. Backend already includes `isTrial`
  // in the response (device.service.ts ~2192-2204); we just forward it through
  // useSubscriptionModal into dialogContext.
  //
  // Fallback chain when `isTrial` isn't present (legacy callers / tests):
  //   1. codeStatus 1000 (AERICAST_TRIAL) or 1006 (ADDON_ENABLE_TRIAL_NOT_ENOUGH)
  //      → scio1 parity (aericastAssignModal.html), kept for callers that only
  //      forward codeStatus.
  //   2. `!thisAddon?.isActive` — last-resort heuristic from local billing cache.
  const codeStatus = dialogContext.codeStatus as number | undefined;
  const isTrialFromContext = dialogContext.isTrial as boolean | undefined;

  // #1174 reopen — the backend `isTrial` flag is overloaded for both
  // "trial available to start" AND "trial currently running" (see
  // device.service.ts ~2192-2204). When the local billing cache shows
  // the addon subscription is already active and in trial state
  // (mirrors AddonList.tsx:288 `isInActiveTrial` pattern), force
  // Activate Trial CTA OFF. Falls through to the paid-subscriber path:
  // Add Licenses becomes primary, body copy switches to license-update.
  const isInActiveTrial = !!(thisAddon?.isTrial && thisAddon?.isActive);

  const isTrialAvailable =
    !isInActiveTrial &&
    (isTrialFromContext !== undefined
      ? isTrialFromContext
      : codeStatus !== undefined
        ? codeStatus === DEVICE_CODE_STATUS.AERICAST_TRIAL ||
          codeStatus === DEVICE_CODE_STATUS.ADDON_ENABLE_TRIAL_NOT_ENOUGH
        : !thisAddon?.isActive);

  // PW04 — Free plan + role gating (scio1 parity, aericastAssignModal.html:5/25/41/45).
  // Free-plan accounts cannot activate paid add-on trials; they must upgrade first.
  // Non-root admins on any plan can only Cancel — they don't have billing authority.
  const isFreePlan = planFeature === PLAN_FEATURES.FREE_PLAN;
  const { isRootAdmin } = usePermissions();

  // Per-addon monthly price from the backend plan-features data — scio1 parity.
  // Tier-aware (Pro vs Standard) and currency-aware (USD/EUR/GBP/etc.) so the
  // copy reflects the customer's actual subscription. Falls back to shipped
  // defaults when the query isn't ready.
  const {
    getPriceByKey,
    getDaysByKey,
    getIsAutoChargeEndTrialByKey,
    getHelpLinkTrialByKey,
    getNumberScreenTrialByKey,
  } = useAddOnPricing();
  // #1181 #14 — pass current addon tier so multi-tier addons (BackgroundMusic Pro)
  // show their actual price instead of the Standard one.
  const priceText = getPriceByKey(addonKey, {
    tier: thisAddon?.planFeature,
    currency,
  });
  // #1181 — trial duration from planFeatures.add_ons[key].numberDayTrial (scio1
  // aericastAssignModal.html:20-21 fallback `|| 30`). Replaces hardcoded "14-day".
  const trialDays = getDaysByKey(addonKey);
  // #1181 #10 — V2/V3/V5 trial pitch selectors (scio1 .html:20-21).
  const isAutoChargeEndTrial = getIsAutoChargeEndTrialByKey(addonKey);
  const helpLinkTrial = getHelpLinkTrialByKey(addonKey);
  // #1181 #8 — OptiDev-specific trial copy (scio1 .html:12-17).
  const isOptiDev = addonKey === 'optidev';
  const optiDevScreens = isOptiDev ? getNumberScreenTrialByKey(addonKey) : 0;

  // Prefer items[0].devices (structured backend payload from addWidgetAddonDevices error),
  // fall back to top-level devices (legacy/shim payloads). Memoized so
  // consumer useCallbacks (handleAddLicenses) have stable deps.
  const items = useMemo(
    () => (dialogContext.items as AddonItem[] | undefined) ?? [],
    [dialogContext.items],
  );
  const devicesFromItems = items[0]?.devices ?? [];
  const devicesFromContext = (dialogContext.devices as AddonDevice[] | undefined) ?? [];
  const allDevices = devicesFromItems.length > 0 ? devicesFromItems : devicesFromContext;
  // #1181 — SCIO1 parity (aericastAssignModal.js:44-52): for codeStatus 1001
  // (license shortfall) when maxQuantityNeedIncrease < devices.length, render
  // only the first N devices — those that actually need licenses. The rest are
  // already covered. Without this slice, scio3 listed every selected screen
  // including covered ones and confused users.
  const itemsNeedIncreaseForDisplay = items[0]?.maxQuantityNeedIncrease;
  const contextNeedIncreaseForDisplay = dialogContext.maxQuantityNeedIncrease as number | undefined;
  const needIncreaseForDisplay = contextNeedIncreaseForDisplay ?? itemsNeedIncreaseForDisplay;
  const devices = useMemo(() => {
    if (
      codeStatus === DEVICE_CODE_STATUS.AERICAST_TRIAL_NOT_ENOUGH &&
      typeof needIncreaseForDisplay === 'number' &&
      needIncreaseForDisplay > 0 &&
      needIncreaseForDisplay < allDevices.length
    ) {
      return allDevices.slice(0, needIncreaseForDisplay);
    }
    return allDevices;
  }, [allDevices, codeStatus, needIncreaseForDisplay]);

  const [startTrial, { loading: startingTrial }] = useMutation(START_TRIAL_ADDON, {
    refetchQueries: ['Me', 'GetCompanyInfo'],
  });
  const [updateAddon, { loading: addingLicenses }] = useMutation(UPDATE_SUBSCRIPTION_ADDON, {
    // SP47 — include GetDevicesAddOn so the subscription-plan "Licenses Used / Total"
    // updates after Add Licenses from the paywall flow.
    refetchQueries: ['Me', 'GetCompanyInfo', 'GetSubscriptionInfoStripe', 'GetDevicesAddOn'],
  });
  const isBusy = startingTrial || addingLicenses;

  const handleActivateTrial = useCallback(async () => {
    if (!addonKey) return;
    try {
      const result = await startTrial({
        variables: { addOn: { key: addonKey.toUpperCase() }, subAccountId },
      });
      const response = (
        result.data as
          | Record<string, { status?: boolean; message?: string } | undefined>
          | undefined
      )?.startTrialAddOn;
      if (response?.status) {
        // Resolve the pending openAddOnRequired promise BEFORE closing so the
        // caller (e.g. AddOnConfigDialog) retries the original mutation.
        resolveAddonRequired({ status: true });
        toast(t('Trial started successfully'));
        closeDialog();
      } else {
        toast.error(response?.message ?? t('Failed to start trial'));
      }
    } catch {
      toast.error(t('Failed to start trial'));
    }
  }, [addonKey, startTrial, subAccountId, closeDialog, t]);

  const handleAddLicenses = useCallback(async () => {
    if (!addonKey) return;

    // #1181 — SCIO1 parity (aericastAssignModal.js:252-256): for codeStatus
    // 1002 (AERICAST_PAID_NOT_ENOUGH) the user already has paid licenses with
    // capacity for the new screens — `ok()` just closes with `status:true` and
    // the caller retries the original push with `forceAssignAC`. NO
    // editSubscriptionAddOn call. Without this guard, scio3 was bumping the
    // subscription's maxQuantity by `devices.length` and over-charging users
    // who already had spare licenses.
    if (codeStatus === DEVICE_CODE_STATUS.AERICAST_PAID_NOT_ENOUGH) {
      resolveAddonRequired({ status: true });
      closeDialog();
      return;
    }

    // scio1 parity: "Add Licenses" processes the purchase immediately using
    // the card on file — NO quantity popup. For multi-tier addons we
    // preserve the user's current tier (BackgroundMusic Standard stays
    // Standard; tier upgrades are a separate settings-page flow).
    //
    // License delta resolution (SP49): prefer the backend-computed shortage
    // at `items[0].maxQuantityNeedIncrease` over `devices.length`. When the
    // user already has unused licenses, `devices.length` includes screens
    // already covered → over-charges. `maxQuantityNeedIncrease` is the
    // exact NEW-license count needed. Matches scio1's
    // aericastAssignModal.ok() → `editSubscriptionAddOn({ number })`.
    const currentQuantity = thisAddon?.maxQuantity ?? 0;
    // Read `maxQuantityNeedIncrease` from both possible locations:
    //   - dialogContext top-level (set by openAddOnRequired when the exception
    //     ext carries it there — the AddOnConfigDialog path)
    //   - items[0].maxQuantityNeedIncrease (items-wrapper path — scio1 shape)
    // Fall back to `devices.length` only when neither is available.
    const contextNeedIncrease = dialogContext.maxQuantityNeedIncrease as number | undefined;
    const itemsNeedIncrease = items[0]?.maxQuantityNeedIncrease;
    const needIncrease = contextNeedIncrease ?? itemsNeedIncrease;
    const delta = needIncrease && needIncrease > 0 ? needIncrease : Math.max(devices.length, 1);
    const newQuantity = currentQuantity + delta;
    const currentTier = thisAddon?.planFeature;
    try {
      const result = await updateAddon({
        variables: {
          addOn: {
            key: addonKey.toUpperCase(),
            maxQuantity: newQuantity,
            ...(currentTier ? { selectedPlanTier: currentTier } : {}),
          },
          subAccountId,
        },
      });
      const response = extractMutationResponse<{
        status?: boolean;
        message?: string;
        code?: number;
      }>(result.data, 'updateSubscriptionAddOn');
      if (response?.status) {
        // Resolve BEFORE closing so AddOnConfigDialog can retry the original
        // addWidgetAddonDevices mutation with the new license headroom.
        resolveAddonRequired({ status: true });
        toast.success(t('Licenses added successfully'));
        closeDialog();
      } else {
        toast.error(response?.message ?? t('Failed to add licenses'));
      }
    } catch {
      toast.error(t('Failed to add licenses'));
    }
  }, [
    addonKey,
    codeStatus,
    thisAddon,
    devices.length,
    items,
    dialogContext.maxQuantityNeedIncrease,
    subAccountId,
    updateAddon,
    closeDialog,
    t,
  ]);

  // PW04 — "Update Subscription" path for Free-plan users (mirrors scio1
  // aericastAssignModal.openSubscriptionV2 → updateSubscription with
  // `minPlanRequired: PLAN_FEATURES.STANDARD`). Opens the wizard pre-set to
  // the Plan step (skipping Number-of-Screens) with the "Upgrade Required"
  // header and the plan list filtered to >= Standard. The wizard already
  // handles all of that automatically when `minPlanRequired` is in context
  // (see useSubscriptionWizard contextMinPlan + SubscriptionWizardDialog title).
  const handleUpgradeFromFree = useCallback(() => {
    resolveAddonRequired({ type: 'goToSub' });
    closeDialog();
    openUpgradeWizard(PLAN_FEATURES.STANDARD);
  }, [closeDialog]);

  // #1181 — "Manage Licenses" path (scio1 aericastAssignModal.goToSub, .js:209-215).
  // Closes the dialog and bubbles `{type: 'goToSub'}` to useSubscriptionModal's
  // handleToggleError, which navigates the user to /app/s/subscription-plan to
  // set up payment / manage existing licenses. Used when the user can't pay
  // in-place (no card on file, on company trial, multi-addon shortage).
  const handleManageLicenses = useCallback(() => {
    resolveAddonRequired({ status: false, type: 'goToSub' });
    closeDialog();
  }, [closeDialog]);

  // Tier-mismatch helpers — derived from items[0] for handleUpgradeTier.
  const firstItem = items[0];
  const firstItemIsTrial = firstItem?.isTrial ?? false;

  // #1181 — "Upgrade Plan" path for ADDON_TIER_MISMATCH (scio1
  // aericastAssignModal.js:257-274). Bumps the addon to the Pro tier via
  // editSubscriptionAddOn with `selectedPlanTier: PRO`. The `number` argument
  // mirrors scio1's special-case formula: 0 when (!isTrial && !needIncrease &&
  // currentTier !== PRO), otherwise needIncrease || devices.length.
  const handleUpgradeTier = useCallback(async () => {
    if (!addonKey) return;
    const currentTier = thisAddon?.planFeature;
    const currentQuantity = thisAddon?.maxQuantity ?? 0;
    // #1198 — `addWidgetAddonDevices` puts maxQuantityNeedIncrease at the TOP
    // level of the error payload, not inside items[0]. Without this
    // dialogContext fallback, paid users with a real license shortage
    // tripped isZeroNumberCase and we sent `maxQuantity: 0`, which the
    // backend interprets as a free tier swap (no Stripe charge) — silently
    // masking insufficient-funds failures and producing a false-success retry.
    const contextNeedIncrease = dialogContext.maxQuantityNeedIncrease as number | undefined;
    const itemNeedIncrease = contextNeedIncrease ?? firstItem?.maxQuantityNeedIncrease ?? 0;
    const itemDevicesCount = firstItem?.devices?.length ?? 0;
    // SCIO1 (.js:264) special-case: trial-not-on-tier with no shortage gets `number: 0`
    // (subscription-only tier change, no license adjustment).
    // #1198 — additionally require devices.length === 0 so a stale backend
    // `needIncrease: 0` (race during tier mismatch) can't trigger a tier-only
    // swap while devices are actively being pushed. Without this guard, a user
    // on Standard with N devices using BM gets flipped to Pro tier with 0
    // licenses, orphaning their existing devices in a weird limbo state.
    const isZeroNumberCase =
      !firstItemIsTrial &&
      !itemNeedIncrease &&
      itemDevicesCount === 0 &&
      currentTier !== PLAN_FEATURES.PRO;
    // #1198 — target = current + backend's reported shortage. When needIncrease
    // is 0 we keep the existing count (tier upgrade only — e.g. "2 Standard →
    // 2 Pro" when the pushed device is already among the 2). The Math.max(..., 1)
    // floor catches the post-downgrade race where currentQuantity=0 AND
    // needIncrease=0, ensuring we never send maxQuantity:0 as a tier-only swap.
    const newQuantity = isZeroNumberCase
      ? 0
      : Math.max(currentQuantity + Math.max(itemNeedIncrease, 0), 1);
    try {
      const result = await updateAddon({
        variables: {
          addOn: {
            key: addonKey.toUpperCase(),
            maxQuantity: newQuantity,
            selectedPlanTier: PLAN_FEATURES.PRO,
          },
          subAccountId,
        },
      });
      const response = extractMutationResponse<{
        status?: boolean;
        message?: string;
      }>(result.data, 'updateSubscriptionAddOn');
      if (response?.status) {
        resolveAddonRequired({ status: true });
        toast.success(t('Plan upgraded successfully'));
        closeDialog();
      } else {
        toast.error(response?.message ?? t('Failed to upgrade plan'));
      }
    } catch {
      toast.error(t('Failed to upgrade plan'));
    }
  }, [
    addonKey,
    thisAddon,
    firstItem,
    firstItemIsTrial,
    dialogContext.maxQuantityNeedIncrease,
    subAccountId,
    updateAddon,
    closeDialog,
    t,
  ]);

  // #1181 — Add Licenses vs Manage Licenses visibility (scio1
  // aericastAssignModal.html:36-37). Show "Add Licenses" only when the user
  // can pay in-place (card on file + not on company trial, or already has
  // paid license capacity). Otherwise show "Manage Licenses" which routes
  // them to the subscription page to set up payment.
  const isCompanyTrial = (dialogContext.isCompanyTrial as boolean | undefined) ?? false;
  const haveCard = (dialogContext.haveCard as boolean | undefined) ?? false;
  const itemsAtMostOne = items.length <= 1;
  const firstItemMaxQuantity = items[0]?.maxQuantity ?? 0;
  const showAddLicenses = isTrialAvailable
    ? !isCompanyTrial
    : (itemsAtMostOne && haveCard && !isCompanyTrial) ||
      (firstItemMaxQuantity > 0 && itemsAtMostOne);
  const showManageLicenses = !showAddLicenses;

  // #1181 — ADDON_TIER_MISMATCH (codeStatus 2002, scio1
  // aericastAssignModal.html:40-45 + .js:257-274). Multi-tier addons
  // (BackgroundMusic Pro, etc.) hit this when an asset requires a higher
  // tier than the user's current addon subscription. Footer is split into
  // three actions:
  //   - Manage Licenses (if isCompanyTrial)
  //   - Upgrade Plan (if !isCompanyTrial && (isTrial || maxQuantityNeedIncrease <= 0))
  //   - Add Licenses (if !isCompanyTrial && !isTrial && maxQuantityNeedIncrease > 0)
  // All three submit `selectedPlanTier: PRO`.
  const isTierMismatch = codeStatus === DEVICE_CODE_STATUS.ADDON_TIER_MISMATCH;
  // #1198 — top-level fallback so the footer picks the right button
  // ("Add Licenses" with shortage > 0 vs "Upgrade Plan" for tier-only swap).
  const firstItemNeedIncrease =
    (dialogContext.maxQuantityNeedIncrease as number | undefined) ??
    firstItem?.maxQuantityNeedIncrease ??
    0;
  const tierShowManage = isTierMismatch && (firstItem?.isCompanyTrial ?? isCompanyTrial);
  const tierShowUpgradePlan =
    isTierMismatch &&
    !(firstItem?.isCompanyTrial ?? isCompanyTrial) &&
    (firstItemIsTrial || firstItemNeedIncrease <= 0);
  const tierShowAddLicenses =
    isTierMismatch &&
    !(firstItem?.isCompanyTrial ?? isCompanyTrial) &&
    !firstItemIsTrial &&
    firstItemNeedIncrease > 0;

  // #1181 — Multi-addon name list (scio1 aericastAssignModal.js:73-82).
  // When push hits multiple addons, build "AeriCast, Video Wall" instead of
  // dropping the second one. Each item carries its `addOnKey`/`key`; map
  // through ADDON_DISPLAY_NAMES with a fallback to the bare key.
  const allAddonNames = useMemo(() => {
    if (items.length === 0) return addonName;
    const names = items
      .map((it) => {
        const key = it.addOnKey ?? it.key ?? '';
        return ADDON_DISPLAY_NAMES[key] ?? key;
      })
      .filter(Boolean);
    return names.length ? names.join(', ') : addonName;
  }, [items, addonName]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (open || isBusy) return;
        // Real close (ESC / outside click / No button). `activeDialog` would
        // still be 'addonRequired' here because none of our handlers reassign
        // it — Add Licenses now processes in-place.
        if (useBillingDialogStore.getState().activeDialog === 'addonRequired') {
          resolveAddonRequired({ close: true });
          closeDialog();
        }
      }}
    >
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-md"
        data-testid="addon-required-dialog"
        // Block ESC / outside-click / X while a mutation is in flight — the
        // ProcessingOverlay is the authoritative UX. DialogContent is already
        // `fixed`-positioned by Radix, so `absolute inset-0` inside the overlay
        // still anchors to the dialog box; no `relative` class needed (adding it
        // would conflict with `fixed` and break centering).
        hideCloseButton={isBusy}
        onEscapeKeyDown={(e) => {
          if (isBusy) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isBusy) e.preventDefault();
        }}
      >
        <ProcessingOverlay visible={isBusy} />
        <DialogHeader>
          <DialogTitle>{t('Add-ons')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2 text-sm">
          {isFreePlan ? (
            // PW04 — Free plan body (scio1 parity, aericastAssignModal.html:25-27)
            <p>{t('freePlanAddOnNotSupportError', { name: addonName })}</p>
          ) : (
            <>
              {/* #1181 — Multi-addon device list (scio1 aericastAssignModal.html:8).
                  Shows the affected screens with the comma-joined addon names so
                  users hitting AeriCast + VideoWall on the same asset see both. */}
              {devices.length > 0 ? (
                <>
                  <p>
                    {t('{{addonName}} add-on license required for:', { addonName: allAddonNames })}
                  </p>
                  <ul className="space-y-1 pl-2">
                    {devices.map((d, i) => (
                      <li key={d._id ?? i}>- {d.name ?? '—'}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p>{t('{{addonName}} add-on license required', { addonName: allAddonNames })}</p>
              )}

              {/* #1181 — Tier mismatch (codeStatus 2002) body copy.
                  scio1 aericastAssignModal.html:22-23: addonTierMismatch when
                  isTrial OR maxQuantityNeedIncrease<=0 (subscription-only upgrade);
                  otherwise the standard "Update license" copy applies. */}
              {isTierMismatch && (firstItemIsTrial || firstItemNeedIncrease <= 0) ? (
                <p>{t('addonTierMismatch', { text: allAddonNames })}</p>
              ) : !isTrialAvailable ? (
                /* #12 — non-trial pitch with totalLicense/numDevices counts
                   (scio1 aericastAssignScreen3 + aericastAssignScreen1). */
                items[0]?.maxQuantity != null && devices.length > 0 ? (
                  <p>
                    {t(
                      'Would you like to update your {{addonName}} add-on license? It will cost an additional {{price}}/mo/add-on screen. You currently have {{totalLicense}} of {{numDevices}} licenses needed.',
                      {
                        addonName: allAddonNames,
                        price: priceText,
                        totalLicense: items[0].maxQuantity ?? 0,
                        numDevices: devices.length,
                      },
                    )}
                  </p>
                ) : (
                  <p>
                    {t(
                      'Would you like to update your {{addonName}} add-on license? It will cost an additional {{price}}/mo/add-on screen.',
                      { addonName: allAddonNames, price: priceText },
                    )}
                  </p>
                )
              ) : isOptiDev ? (
                /* #8 — OptiDev base-plan copy. scio1 .html:13-17. */
                <p>
                  {t('subscriptionTrialAddOnOptiDev', {
                    currency: currencySymbol,
                    price: priceText.replace(/^[^\d]+/, ''),
                    interval: t('mo'),
                    screens: optiDevScreens,
                  })}
                </p>
              ) : isAutoChargeEndTrial && helpLinkTrial ? (
                /* #10 V3 — autoCharge trial with help link. scio1 .html:21. */
                <p>
                  {t(
                    'Activate {{days}}-day Trial for {{name}} app? When trial is complete, each device using {{name}} will cost an additional {{price}} per month. Learn more.',
                    { days: trialDays, name: allAddonNames, price: priceText },
                  )}{' '}
                  <a
                    href={helpLinkTrial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {t('Learn more')}
                  </a>
                </p>
              ) : (
                /* V2/V5 — generic X-day trial pitch. */
                <p>
                  {t(
                    'Activate {{days}}-day Trial for {{name}} app? When trial is complete, each device using {{name}} will cost an additional {{price}} per month.',
                    { days: trialDays, name: allAddonNames, price: priceText },
                  )}
                </p>
              )}

              {/* #1181 — non-root-admin warning (scio1 aericastAssignModal.html:27-29).
                  Renders inside the paid-plan body so users without billing authority
                  see WHY the action buttons are hidden, not just an empty Cancel-only
                  footer. */}
              {!isRootAdmin && (
                <p className="text-center text-destructive">
                  <strong>{t('cannotUpgrade')}</strong>
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {isFreePlan && isRootAdmin ? (
            // PW04 state #3 — Free plan + root admin: Cancel / Update Subscription
            // (scio1 aericastAssignModal.html:41-44, vm.openSubscriptionV2())
            <>
              <DialogClose asChild>
                <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                  {t('Cancel')}
                </Button>
              </DialogClose>
              <Button onClick={handleUpgradeFromFree} disabled={isBusy} className="cursor-pointer">
                {t('Update Subscription')}
              </Button>
            </>
          ) : !isRootAdmin ? (
            // PW04 state #4 — non-root-admin: Cancel only (scio1 :45-47, applies to
            // Free OR paid — lower roles have no billing authority)
            <DialogClose asChild>
              <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                {t('Cancel')}
              </Button>
            </DialogClose>
          ) : isTierMismatch ? (
            // #1181 — ADDON_TIER_MISMATCH (codeStatus 2002) footer (scio1
            // aericastAssignModal.html:40-45). Cancel + one of:
            //   - Manage Licenses (isCompanyTrial → goToSub)
            //   - Upgrade Plan (paid + (trial OR no shortage) → editAddon w/ Pro tier)
            //   - Add Licenses (paid + !trial + has shortage → editAddon w/ Pro tier)
            <>
              <DialogClose asChild>
                <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                  {t('Cancel')}
                </Button>
              </DialogClose>
              {tierShowManage && (
                <Button onClick={handleManageLicenses} disabled={isBusy} className="cursor-pointer">
                  {t('Manage Licenses')}
                </Button>
              )}
              {tierShowUpgradePlan && (
                <Button onClick={handleUpgradeTier} disabled={isBusy} className="cursor-pointer">
                  {t('Upgrade Plan')}
                </Button>
              )}
              {tierShowAddLicenses && (
                <Button onClick={handleUpgradeTier} disabled={isBusy} className="cursor-pointer">
                  {t('Add Licenses')}
                </Button>
              )}
            </>
          ) : (
            // Default — paid plan + root admin: No / [Manage Licenses | Add Licenses] / [Activate Trial]
            // SCIO1 parity (aericastAssignModal.html:34-38). Manage and Add Licenses are
            // mutually exclusive: Add shows when user can pay in-place (card on file +
            // not on company trial, OR has paid license capacity); Manage shows
            // otherwise and routes to /app/s/subscription-plan via 'goToSub'.
            <>
              <DialogClose asChild>
                <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                  {t('No')}
                </Button>
              </DialogClose>
              {showManageLicenses && (
                <Button
                  variant="outline"
                  onClick={handleManageLicenses}
                  disabled={isBusy}
                  className="cursor-pointer"
                >
                  {t('Manage Licenses')}
                </Button>
              )}
              {showAddLicenses && (
                <Button
                  variant={isTrialAvailable ? 'outline' : 'default'}
                  onClick={handleAddLicenses}
                  disabled={isBusy}
                  className="cursor-pointer"
                >
                  {t('Add Licenses')}
                </Button>
              )}
              {isTrialAvailable && (
                <Button onClick={handleActivateTrial} disabled={isBusy} className="cursor-pointer">
                  {t('Activate Trial')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
